import React, { useEffect, useRef } from 'react'
import { EditorView, basicSetup } from 'codemirror'
import { EditorState, Extension } from '@codemirror/state'
import { oneDark } from '@codemirror/theme-one-dark'

interface Props {
	value: string
	onChange: (value: string) => void
	extensions: Extension[]
}

export const CodeMirrorEditor = ({ value, onChange, extensions }: Props) => {
	const editorRef = useRef<HTMLDivElement>(null)
	const viewRef = useRef<EditorView | null>(null)

	// Effect to initialize the editor
	useEffect(() => {
		if (!editorRef.current) return

		const state = EditorState.create({
			doc: value,
			extensions: [
				basicSetup,
				oneDark,
				...extensions,
				EditorView.theme({
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'&': {
						height: '100%',
						fontSize: '14px',
						fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
					},
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'.cm-scroller': {
						overflow: 'auto',
					},
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'.cm-focused': {
						outline: 'none',
					},
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'.cm-editor': {
						height: '100%',
					},
				}),
				EditorView.updateListener.of((update) => {
					if (update.docChanged) {
						const newValue = update.state.doc.toString()
						onChange(newValue)
					}
				}),
			],
		})

		const view = new EditorView({
			state,
			parent: editorRef.current,
		})

		viewRef.current = view

		return () => {
			view.destroy()
			viewRef.current = null
		}
	}, [extensions])

	// Effect to synchronize React value prop with editor state
	useEffect(() => {
		const view = viewRef.current
		if (!view) return

		const editorValue = view.state.doc.toString()

		if (value !== editorValue) {
			view.dispatch({
				changes: { from: 0, to: editorValue.length, insert: value || '' },
			})
		}
	}, [value])

	return (
		<div
			ref={editorRef}
			style={{
				height: '100%',
				width: '100%',
			}}
		/>
	)
}

export default CodeMirrorEditor
